/**
 * DevInspector — AI Dev Inspector (Enhanced)
 *
 * Trigger: Ctrl+RightClick OR Shift+RightClick on ANY element.
 * Standard RightClick = normal browser context menu (no conflict).
 *
 * Shows:
 *   ① Screen / Form Name  (from data-testid + ALL_PROC_CONFIGS)
 *   ② JSX File + Component (from data-dev-file / data-dev-component)
 *   ③ Line Number          (from React fiber _debugSource — dev mode)
 *   ④ Control Name & Type  (label, input type, placeholder, value)
 *   ⑤ data-testid chain    (nearest up to root)
 *   ⑥ CSS classes + computed styles
 *   ⑦ "Copy for AI Agent" — formats a structured task description
 *
 * Admin-only (SUPER_ADMIN / ssadmin / syscliq users)
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, Copy, Check, Code2, FileCode, Tag, Palette, Layers, ChevronRight } from 'lucide-react';

// ─── Screen registry: testid → human-readable info ─────────────────────────
const SCREEN_MAP = {
  'purchase-invoice-v2':    { name:'Purchase Invoice Form',          file:'PurchaseInvoiceV2Screen.jsx' },
  'pi-list-':               { name:'Purchase Invoice List',          file:'PurchaseInvoiceV2Screen.jsx' },
  'grn-form-':              { name:'GRN Form',                       file:'GRNScreen.jsx' },
  'grn-list-':              { name:'GRN List',                       file:'GRNScreen.jsx' },
  'grc-form-':              { name:'GRC Form',                       file:'GRCScreen.jsx' },
  'proc-grid-':             { name:'ERPAdvancedProcGrid (List)',      file:'ERPAdvancedProcGrid.jsx' },
  'proc-item-grid':         { name:'ProcItemGrid (Item Entry Grid)',  file:'ProcItemGrid.jsx' },
  'proc-form-':             { name:'Procurement Form',               file:'ProcurementForm.jsx' },
  'proc-doc-info-form':     { name:'Document Information Section',   file:'ProcurementForm.jsx' },
  'grn-doc-info-form':      { name:'Document Information Section',   file:'GRNScreen.jsx' },
  'proc-inspector-panel':   { name:'Item Inspector Panel',           file:'ProcItemGrid.jsx' },
  'grn-inspector-panel':    { name:'Item Inspector Panel (GRN)',     file:'GRNScreen.jsx' },
  'grc-inspector-panel':    { name:'Item Inspector Panel (GRC)',     file:'GRCScreen.jsx' },
  'row-context-menu':       { name:'Row Context Menu',               file:'ProcItemGrid.jsx' },
  'unit-identifier-table':  { name:'IMEI/Serial Unit Identifier',    file:'UnitIdentifierModal.jsx' },
  'img-drop-zone':          { name:'Image Upload Zone',              file:'ImageManagerModal.jsx' },
  'sizecfg-':               { name:'F10 Size Configuration',         file:'SizeConfigModal.jsx' },
  'new-item-modal-':        { name:'F2/F3 New Item Modal',           file:'NewItemModal.jsx' },
  'supplier-master':        { name:'Supplier Master Grid',           file:'SupplierMasterScreen.jsx' },
  'erp-grid':               { name:'ERP Advanced Grid',             file:'ERPAdvancedGrid.jsx' },
  'voucher-':               { name:'Finance Voucher Screen',         file:'VoucherScreen.js' },
};

const PROC_SCREEN_IDS = {
  '4.10.3':'Purchase Invoice (Direct)',       '4.10.1':'Purchase Invoice Against GRC',
  '4.10.2':'Purchase Invoice Against PO',    '4.10.4':'Purchase Returns',
  '4.8.17':'PI Return (New UI)',
  '4.8.14':'GRN Against P.O (New UI)',       '4.8.15':'GRN Against P.I (New UI)',
  '4.8.16':'GRN Return (New UI)',
  '4.8.1':'GRC Direct',   '4.8.2':'GRC Against PO',  '4.8.3':'GRC Template',
  '4.8.5':'GRC Returns Challan', '4.8.6':'GRC Return Against GRC',
  '4.8.8':'GRC Returns Packing', '4.8.13':'GRC Garments', '4.21.5':'GRC Against PB',
};

// ─── React fiber source (line number — dev mode) ───────────────────────────
function getReactFiberSource(el) {
  try {
    const fiberKey = Object.keys(el).find(k => k.startsWith('__reactFiber$') || k.startsWith('__reactInternalInstance$'));
    if (!fiberKey) return null;
    let fiber = el[fiberKey];
    const seen = new Set();
    while (fiber && !seen.has(fiber)) {
      seen.add(fiber);
      if (fiber._debugSource) return fiber._debugSource;
      if (fiber._debugOwner?._debugSource) return fiber._debugOwner._debugSource;
      fiber = fiber.return;
    }
  } catch (_) {}
  return null;
}

// ─── Control detection ──────────────────────────────────────────────────────
function detectControl(el) {
  const tag = el.tagName?.toLowerCase();
  let type = tag || 'div';
  let label = null;
  let value = null;
  let placeholder = null;
  let fieldId = el.id || el.name || null;
  let testId = el.dataset?.testid || null;

  if (tag === 'input') {
    type = `input[${el.type || 'text'}]`;
    value = el.value ? `"${el.value.substring(0,40)}"` : null;
    placeholder = el.placeholder || null;
  } else if (tag === 'select') {
    type = 'select';
    const opt = el.options[el.selectedIndex];
    value = opt ? `"${opt.text}"` : null;
  } else if (tag === 'button') {
    type = 'button';
    label = (el.innerText||'').trim().substring(0,60) || null;
  } else if (tag === 'textarea') {
    type = 'textarea';
    value = el.value ? `"${el.value.substring(0,40)}"` : null;
  }

  // Find label via data-testid hint
  if (!label && testId) label = testId.replace(/-/g,' ').replace(/\b\w/g,c=>c.toUpperCase());

  // Walk up to find nearest pi2-field, pi2-edrow, erp-form-row label
  let cur = el.parentElement;
  let depth = 0;
  while (cur && cur !== document.body && depth < 6) {
    const cls = cur.className || '';
    if (cls.includes('pi2-field') || cls.includes('pi2-edrow') || cls.includes('erp-form-row') || cls.includes('field')) {
      const lEl = cur.querySelector('label');
      if (lEl && !label) label = lEl.innerText?.trim() || null;
      break;
    }
    cur = cur.parentElement; depth++;
  }

  return { type, label, fieldId, testId, value, placeholder };
}

// ─── Screen/form context detection ─────────────────────────────────────────
function detectFormContext(el) {
  // Find data-testid on element and ancestors
  const testIds = [];
  let cur = el;
  let depth = 0;
  while (cur && cur !== document.body && depth < 20) {
    if (cur.dataset?.testid) testIds.unshift(cur.dataset.testid);
    cur = cur.parentElement; depth++;
  }

  // Find screen context from page status bar (Screen Id badge)
  let screenId = null;
  const statusBar = document.querySelector('.pi2-statusbar');
  if (statusBar) {
    const match = statusBar.textContent?.match(/Screen Id:\s*([\d.]+)/);
    if (match) screenId = match[1];
  }
  const screenName = screenId ? (PROC_SCREEN_IDS[screenId] || `Screen ${screenId}`) : null;

  // Map testids to known screens
  let screenInfo = null;
  for (const tid of testIds) {
    for (const [pattern, info] of Object.entries(SCREEN_MAP)) {
      if (tid === pattern || tid.startsWith(pattern)) {
        screenInfo = { ...info, testId: tid };
        break;
      }
    }
    if (screenInfo) break;
  }

  return {
    screenId:   screenId || null,
    screenName: screenName || null,
    screenInfo: screenInfo || null,
    testIdChain: testIds.slice(-4), // last 4 relevant testIds
  };
}

// ─── Full metadata collection ───────────────────────────────────────────────
function collectMetadata(el) {
  if (!el || el === document.body) return null;

  let cur = el;
  let component = null, file = null, testId = null, helpControl = null;
  while (cur && cur !== document.body) {
    if (!component && cur.dataset?.devComponent) component = cur.dataset.devComponent;
    if (!file && cur.dataset?.devFile)       file = cur.dataset.devFile;
    if (!testId && cur.dataset?.testid)      testId = cur.dataset.testid;
    if (!helpControl && cur.dataset?.helpControl) helpControl = cur.dataset.helpControl;
    cur = cur.parentElement;
  }

  const reactSrc = getReactFiberSource(el);
  const control   = detectControl(el);
  const form      = detectFormContext(el);

  // Resolve file from react source or SCREEN_MAP
  const resolvedFile = file
    || (reactSrc?.fileName ? reactSrc.fileName.split('/').pop() : null)
    || form.screenInfo?.file
    || null;

  const cs = window.getComputedStyle(el);
  const styles = {
    fontSize:        cs.fontSize,
    fontWeight:      cs.fontWeight,
    color:           cs.color,
    backgroundColor: cs.backgroundColor,
    borderRadius:    cs.borderRadius,
    padding:         cs.padding,
    display:         cs.display,
    width:           cs.width,
    height:          cs.height,
  };

  const classes = Array.from(el.classList).filter(c => c.length < 80);

  return {
    form,
    component:    component || (reactSrc?.fileName?.split('/').pop()?.replace('.jsx','').replace('.js','')) || '(unknown)',
    file:         resolvedFile || '(unknown)',
    lineNumber:   reactSrc?.lineNumber || null,
    columnNumber: reactSrc?.columnNumber || null,
    control,
    testId:       testId || null,
    helpControl:  helpControl || null,
    element: {
      tagName:   el.tagName?.toLowerCase(),
      id:        el.id || null,
      classes,
      innerText: (el.innerText || '').substring(0, 100).trim() || null,
    },
    styles,
    timestamp: new Date().toISOString(),
  };
}

// ─── Copy helpers ───────────────────────────────────────────────────────────
async function copyToClipboard(text) {
  try {
    if (navigator.clipboard?.writeText) { await navigator.clipboard.writeText(text); return true; }
    const ta = document.createElement('textarea');
    ta.value = text; ta.style.position = 'fixed'; ta.style.opacity = '0';
    document.body.appendChild(ta); ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta); return ok;
  } catch { return false; }
}

// ─── Main component ─────────────────────────────────────────────────────────
export function DevInspector({ userRole = '' }) {
  const resolvedRole = (() => {
    if (userRole) return userRole;
    try {
      const raw = localStorage.getItem('retailcliq_user') || sessionStorage.getItem('retailcliq_user') ||
                  localStorage.getItem('rcq_user') || sessionStorage.getItem('rcq_user');
      if (raw) { const u = JSON.parse(raw); return u?.role || u?.user?.role || ''; }
    } catch (_) {}
    return '';
  })();
  const resolvedUsername = (() => {
    try {
      const raw = localStorage.getItem('rcq_user') || sessionStorage.getItem('rcq_user') ||
                  localStorage.getItem('retailcliq_user') || sessionStorage.getItem('retailcliq_user');
      if (raw) { const u = JSON.parse(raw); return (u?.name || u?.username || '').toLowerCase(); }
    } catch (_) {}
    return '';
  })();
  const roleLower = (resolvedRole || '').toLowerCase();
  const isAdmin =
    roleLower === 'super_admin' || roleLower === 'superadmin' || roleLower === 'sys-admin' ||
    roleLower === 'admin' || roleLower === 'licensor' ||
    resolvedUsername.startsWith('ssadmin') || resolvedUsername.startsWith('syscliq') ||
    resolvedUsername.startsWith('sysadmin');

  const [visible, setVisible] = useState(false);
  const [pos, setPos]   = useState({ x: 0, y: 0 });
  const [meta, setMeta] = useState(null);
  const [copied, setCopied] = useState('');
  const panelRef = useRef(null);

  const handleContextMenu = useCallback((e) => {
    if (!isAdmin) return;
    if (!e.ctrlKey && !e.shiftKey) return;
    e.preventDefault(); e.stopPropagation();
    const metadata = collectMetadata(e.target);
    setMeta(metadata);
    const vw = window.innerWidth, vh = window.innerHeight;
    const pw = 400, ph = 580;
    setPos({ x: Math.max(8, Math.min(e.clientX + 8, vw - pw - 16)), y: Math.max(8, Math.min(e.clientY + 8, vh - ph - 16)) });
    setVisible(true); setCopied('');
  }, [isAdmin]);

  useEffect(() => {
    if (!visible) return;
    const onKey = (e) => { if (e.key === 'Escape') setVisible(false); };
    const onMouse = (e) => { if (panelRef.current && !panelRef.current.contains(e.target)) setVisible(false); };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onMouse);
    return () => { document.removeEventListener('keydown', onKey); document.removeEventListener('mousedown', onMouse); };
  }, [visible]);

  useEffect(() => {
    document.addEventListener('contextmenu', handleContextMenu, true);
    return () => document.removeEventListener('contextmenu', handleContextMenu, true);
  }, [handleContextMenu]);

  async function doCopy(text, key) {
    const ok = await copyToClipboard(text);
    if (ok) { setCopied(key); setTimeout(() => setCopied(''), 2500); }
    else window.prompt('Copy (Ctrl+C):', text);
  }

  function copyJson() {
    doCopy(JSON.stringify(meta, null, 2), 'json');
  }

  function copyAiPrompt() {
    if (!meta) return;
    const { form, component, file, lineNumber, control, testId, element } = meta;
    const formLine = form.screenName ? `**Screen:** ${form.screenName} (${form.screenId || ''})` : '';
    const fileLine = file !== '(unknown)' ? `**File:** ${file}${lineNumber ? ` · Line ~${lineNumber}` : ''}` : '';
    const compLine = `**Component:** ${component}`;
    const ctrlLine = control.label
      ? `**Control:** ${control.label} — ${control.type}${control.testId ? ` (testid: ${control.testId})` : ''}`
      : `**Element:** <${element.tagName}>${testId ? ` (testid: ${testId})` : ''}`;
    const valLine  = control.value ? `**Current value:** ${control.value}` : '';
    const classLine = element.classes.length > 0 ? `**CSS classes:** ${element.classes.slice(0,6).join(' ')}` : '';

    const prompt = [
      '## AI Dev Inspector — Element Context',
      '',
      formLine, fileLine, compLine, ctrlLine,
      valLine, classLine,
      form.testIdChain?.length ? `**TestID chain:** ${form.testIdChain.join(' → ')}` : '',
      '',
      '---',
      '### Task for Agent:',
      '> [Describe what you want to change/fix/add here]',
    ].filter(Boolean).join('\n');

    doCopy(prompt, 'prompt');
  }

  if (!isAdmin || !visible || !meta) return null;

  const { form, component, file, lineNumber, control, testId, element, styles } = meta;

  return (
    <div ref={panelRef} data-testid="dev-inspector-panel"
      style={{ position:'fixed', left:pos.x, top:pos.y, width:400, maxHeight:580, overflowY:'auto',
        background:'#0f172a', color:'#e2e8f0', borderRadius:10, zIndex:99999, fontSize:12,
        fontFamily:"'SF Mono','Fira Code',monospace",
        boxShadow:'0 20px 60px rgba(0,0,0,.65), 0 0 0 1px rgba(99,102,241,.35)' }}>

      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'8px 12px',
        background:'#1e293b', borderRadius:'10px 10px 0 0', borderBottom:'1px solid #334155' }}>
        <div style={{ display:'flex', alignItems:'center', gap:6, color:'#818cf8', fontWeight:700, fontSize:11 }}>
          <Code2 size={13}/> AI Dev Inspector
          {lineNumber && <span style={{ marginLeft:6, background:'rgba(99,102,241,.2)', color:'#a5b4fc', padding:'1px 5px', borderRadius:3, fontSize:10 }}>Line {lineNumber}</span>}
        </div>
        <button onClick={()=>setVisible(false)} style={{ background:'none', border:'none', cursor:'pointer', color:'#64748b', display:'flex' }}>
          <X size={14}/>
        </button>
      </div>

      <div style={{ padding:'10px 12px', display:'flex', flexDirection:'column', gap:8 }}>

        {/* ① Screen / Form */}
        <Section icon={<Layers size={11}/>} label="Screen / Form Context">
          {form.screenName && <Row label="Screen" value={form.screenName} accent="#34d399"/>}
          {form.screenId   && <Row label="Screen ID" value={form.screenId} accent="#fbbf24" mono/>}
          {form.screenInfo?.name && !form.screenName && <Row label="Section" value={form.screenInfo.name} accent="#34d399"/>}
          {form.testIdChain?.length > 0 && (
            <div style={{ marginTop:3, display:'flex', flexWrap:'wrap', gap:3, alignItems:'center' }}>
              {form.testIdChain.map((t,i)=>(
                <React.Fragment key={t}>
                  <span style={{ background:'#1e293b', color:'#f472b6', padding:'1px 5px', borderRadius:3, fontSize:10 }}>{t}</span>
                  {i < form.testIdChain.length-1 && <ChevronRight size={9} color="#475569"/>}
                </React.Fragment>
              ))}
            </div>
          )}
        </Section>

        {/* ② JSX Component + File + Line */}
        <Section icon={<FileCode size={11}/>} label="Component / File">
          <Row label="Component" value={component} accent="#818cf8"/>
          <Row label="File" value={file} accent="#94a3b8" mono/>
          {lineNumber && <Row label="Line" value={`${lineNumber}${meta.columnNumber ? `:${meta.columnNumber}` : ''}`} accent="#fbbf24"/>}
          {!lineNumber && <Row label="Line" value="(run in dev mode for exact line)" accent="#475569"/>}
        </Section>

        {/* ③ Control name + type */}
        {(control.label || control.type !== 'div') && (
          <Section icon={<Tag size={11}/>} label="Control">
            {control.label      && <Row label="Label" value={control.label} accent="#34d399"/>}
            <Row label="Type" value={control.type} accent="#60a5fa"/>
            {control.testId     && <Row label="TestID" value={control.testId} accent="#f472b6"/>}
            {control.fieldId    && <Row label="Field ID" value={control.fieldId} accent="#94a3b8" mono/>}
            {control.placeholder&& <Row label="Placeholder" value={`"${control.placeholder}"`} accent="#94a3b8"/>}
            {control.value      && <Row label="Value" value={control.value} accent="#fbbf24"/>}
          </Section>
        )}

        {/* ④ Element */}
        <Section icon={<Tag size={11}/>} label="DOM Element">
          <Row label="Tag" value={`<${element.tagName}>`} accent="#34d399"/>
          {element.id    && <Row label="ID" value={`#${element.id}`} accent="#fbbf24"/>}
          {testId        && !control.testId && <Row label="TestID" value={testId} accent="#f472b6"/>}
          {meta.helpControl && <Row label="HelpCtrl" value={meta.helpControl} accent="#60a5fa"/>}
          {element.innerText && element.tagName !== 'input' && <Row label="Text" value={`"${element.innerText}"`} accent="#94a3b8"/>}
        </Section>

        {/* ⑤ CSS Classes (compact) */}
        {element.classes.length > 0 && (
          <Section icon={<Palette size={11}/>} label="CSS Classes">
            <div style={{ display:'flex', flexWrap:'wrap', gap:3, marginTop:3 }}>
              {element.classes.slice(0,12).map((c,i)=>(
                <span key={i} style={{ background:'#1e293b', color:'#7dd3fc', padding:'1px 5px', borderRadius:3, fontSize:10 }}>{c}</span>
              ))}
              {element.classes.length > 12 && <span style={{ color:'#475569', fontSize:10 }}>+{element.classes.length-12} more</span>}
            </div>
          </Section>
        )}

        {/* ⑥ Key styles */}
        <Section icon={<Palette size={11}/>} label="Computed Styles">
          {['fontSize','fontWeight','color','backgroundColor','borderRadius','padding','display'].map(k =>
            styles[k] && styles[k]!=='rgba(0, 0, 0, 0)' && styles[k]!=='0px' ? (
              <Row key={k} label={k} value={styles[k]} accent="#94a3b8"/>
            ) : null
          )}
        </Section>

        {/* ⑦ Actions */}
        <div style={{ display:'flex', gap:6, paddingTop:8, borderTop:'1px solid #1e293b' }}>
          <ActionBtn label={copied==='json'?'Copied!':'Copy JSON'} onClick={copyJson} active={copied==='json'} color="#6366f1"/>
          <ActionBtn label={copied==='prompt'?'Copied!':'Copy for AI'} onClick={copyAiPrompt} active={copied==='prompt'} color="#7c3aed"/>
        </div>

        <div style={{ fontSize:10, color:'#475569', textAlign:'right', marginTop:2 }}>
          Ctrl+RightClick any element • Esc to close
        </div>
      </div>
    </div>
  );
}

function Section({ icon, label, children }) {
  return (
    <div>
      <div style={{ display:'flex', alignItems:'center', gap:4, color:'#64748b', fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:4 }}>
        {icon} {label}
      </div>
      {children}
    </div>
  );
}

function Row({ label, value, accent='#e2e8f0', mono=false }) {
  return (
    <div style={{ display:'flex', gap:6, marginBottom:2, alignItems:'flex-start' }}>
      <span style={{ color:'#475569', minWidth:72, flexShrink:0 }}>{label}:</span>
      <span style={{ color:accent, wordBreak:'break-all', fontFamily:mono?'monospace':'inherit', fontSize:11 }}>{value}</span>
    </div>
  );
}

function ActionBtn({ label, onClick, active, color }) {
  return (
    <button onClick={onClick}
      style={{ flex:1, padding:'5px 8px', borderRadius:5, border:'none', cursor:'pointer',
        background:active?'#16a34a':color, color:'white', fontSize:10, fontWeight:600,
        transition:'background 0.15s' }}>
      {label}
    </button>
  );
}
